import CustomHead from '@/components/CustomHead';
import { BrandLogo } from '@/components/BrandLogo';
import { useBrand } from '@/lib/brand/BrandContext';
import Link from 'next/link';
import type { ReactNode } from 'react';

type AuthRecoveryShellProps = {
  children: ReactNode;
  description: string;
  title: string;
};

export const AuthRecoveryShell = ({
  children,
  description,
  title,
}: AuthRecoveryShellProps) => {
  const brand = useBrand();
  const isNyla = brand.brandKey === 'nyla';
  const homeHref = isNyla ? '/' : 'https://exploremindlabs.com';

  if (!isNyla) {
    return (
      <main className="flex min-h-screen flex-col justify-center px-6 py-12 lg:px-8">
        <CustomHead title={title} description={description} />
        <div className="text-center sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="text-center text-3xl text-neutral-900">{title}</h2>
        </div>
        <div className="mt-8 flex w-full items-center justify-center">
          <div className="w-full max-w-md justify-center rounded bg-white px-4 py-5 align-middle shadow">
            {children}
          </div>
        </div>
      </main>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <CustomHead
        title={`${title} - ${brand.appName}`}
        description={description}
      />
      <div className="mx-auto max-w-6xl px-4 py-6">
        <Link href={homeHref} aria-label={`${brand.appName} home`}>
          <BrandLogo />
        </Link>
      </div>
      <main className="mx-auto flex min-h-[calc(100vh-104px)] max-w-6xl items-center px-4 pb-12">
        <div className="grid w-full gap-8 lg:grid-cols-[minmax(0,1fr)_28rem] lg:items-center">
          <section
            className="nyla-display hidden rounded-3xl border-2 border-border p-8 text-primary-foreground shadow-[var(--brand-shadow-playful)] lg:block"
            style={{ background: 'var(--brand-gradient-hero)' }}
          >
            <p className="text-lg font-bold opacity-95">
              Mission access support
            </p>
            <h1 className="mt-2 max-w-xl text-4xl font-extrabold leading-tight">
              Reset your launch credentials.
            </h1>
            <div className="mt-8 grid max-w-lg grid-cols-2 gap-3">
              <div className="rounded-2xl bg-white/20 p-4 backdrop-blur">
                <div className="text-sm font-bold opacity-80">Signal</div>
                <div className="mt-1 text-2xl font-extrabold">Secure</div>
              </div>
              <div className="rounded-2xl bg-white/20 p-4 backdrop-blur">
                <div className="text-sm font-bold opacity-80">Missions</div>
                <div className="mt-1 text-2xl font-extrabold">Ready</div>
              </div>
            </div>
          </section>
          <section className="mx-auto w-full max-w-md rounded-3xl border-2 border-border bg-card/95 p-5 shadow-[var(--brand-shadow-playful)] backdrop-blur">
            <div className="mb-5">
              <p className="nyla-display text-sm font-bold text-muted-foreground">
                Nyla Nova STEM Missions
              </p>
              <h1 className="mt-1 text-2xl font-extrabold text-foreground">
                {title}
              </h1>
            </div>
            {children}
          </section>
        </div>
      </main>
    </div>
  );
};
